import { createContext, useContext } from 'react';
import { usePage } from '@inertiajs/react';
import { DOMAIN_META } from '@/Components/Grants/grantsCatalog';

/**
 * The member's grants on the current team, as the server shares them in
 * `teamAccess.grants`: `{ ability: null | { ontologies: [...] } }`, null
 * meaning team-wide. Mirrors App\Support\Auth\Grants::allows(); the server
 * enforces, this only decides what to show.
 */
export function allows(grants, ability, ontologyId = null) {
    if (!grants || !Object.prototype.hasOwnProperty.call(grants, ability)) {
        return false;
    }

    const scope = grants[ability];

    if (scope === null || scope === undefined) {
        return true;
    }

    return ontologyId !== null && Array.isArray(scope.ontologies) && scope.ontologies.includes(ontologyId);
}

export function isScoped(grants, ability) {
    return Boolean(grants && grants[ability] && Array.isArray(grants[ability].ontologies));
}

export function scopedOntologies(grants, ability) {
    return isScoped(grants, ability) ? grants[ability].ontologies : null;
}

// What the member's role cannot do, by the ability's level.
const VERBS = { read: 'see', write: 'change', use: 'use' };

/** An ability's area in the grants catalog's words ("Partner links"), else its domain in words. */
function areaTitle(domain) {
    const title = DOMAIN_META[domain]?.title;

    if (title) return title;

    const words = String(domain ?? '').replace(/[_-]+/g, ' ').trim();

    return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'this';
}

/**
 * The one sentence the app uses for an ability the member's grants do not
 * cover, in plain words: what their role cannot do, in the grants
 * catalog's name for the area, and who can help. Never the ability key.
 * Shared so a menu item and a button say the same thing about the same gap.
 */
export function gateReason(ability, ontologyId = null) {
    const [domain, level] = String(ability ?? '').split(':');
    const verb = VERBS[level] ?? 'use';

    return `Your role cannot ${verb} ${areaTitle(domain)}${ontologyId ? ' in this ontology' : ''}. Ask a team owner or admin.`;
}

const UNSUSPENDED = { level: 'none', source: null, canCreate: true };
const CREATE_SUSPENDED = 'Suspended: nothing new can be created';
const READ_ONLY = 'Suspended: this team is read-only';
export const SUPPLIER_ONLY = 'Teams without a company cannot issue passports. Ask Synex to set up a company for your team.';

/**
 * A stand-in for the page's `teamAccess`, for a subtree that must not show the
 * visitor's own access: the marketing site's product vignettes render real app
 * components for guests (who hold no grants) and for signed-in members (who
 * hold their own), and both would otherwise see those components locked or
 * live. Provided, it replaces `teamAccess` whole (grants, suspension,
 * issuesPassports and isOwner) for everything beneath it; a key it leaves out
 * takes the same default a page without the prop gets. The default `null`
 * leaves every page reading its own props, as before.
 */
export const GrantsOverrideContext = createContext(null);

export default function useGrants() {
    const override = useContext(GrantsOverrideContext);
    const { props } = usePage();
    const access = override ?? props?.teamAccess;
    const grants = access?.grants || {};
    // `{ level, source, canCreate }` as the server shares it; a page rendered
    // without the prop (a test, a guest view) is unsuspended.
    const suspension = access?.suspension || UNSUSPENDED;
    const readOnly = suspension.level === 'write' || suspension.level === 'full';
    // False only for a team without a company (a supplier or partner team),
    // which issues no passports; a page rendered without the prop issues them.
    const issuesPassports = access?.issuesPassports ?? true;

    // Why a control is unavailable, or null when it is not. The suspension is
    // named before the grant: the write ceiling strips every *:write before
    // the prop is shared, so the grant alone would blame the member's role
    // for the team's state. The create tier strips nothing, so `creates` is
    // the one way a held grant is still refused. `issues` marks the entry
    // points that issue passports, which a supplier team never may.
    const reasonFor = (ability, ontologyId = null, { creates = false, issues = false } = {}) => {
        if (creates && !suspension.canCreate) {
            return CREATE_SUSPENDED;
        }

        if (issues && !issuesPassports) {
            return SUPPLIER_ONLY;
        }

        if (ability && readOnly && ability.endsWith(':write')) {
            return READ_ONLY;
        }

        if (!ability || allows(grants, ability, ontologyId)) {
            return null;
        }

        return gateReason(ability, ontologyId);
    };

    return {
        grants,
        isOwner: Boolean(access?.isOwner),
        suspension,
        issuesPassports,
        can: (ability, ontologyId = null) => allows(grants, ability, ontologyId),
        isScoped: (ability) => isScoped(grants, ability),
        scopedOntologies: (ability) => scopedOntologies(grants, ability),
        reasonFor,
    };
}
